// formatDateTime: i18n-fähiges Datum-/Zeit-Format-Util für die
// Listen-/Inbox-Datumsanzeige. Generic, tenant-agnostisch.
//
// Ersetzt die je Cut-App kopierte `formatInboxDateTime`-Funktion
// (mednet-practice/identity/scheduling). Default-Format entspricht der
// bisherigen Inbox-Darstellung `DD.MM.YYYY HH:MM`, lässt sich aber per
// locale/timeZone an die jeweilige Umgebung anpassen.

export interface FormatDateTimeOptions {
  // BCP-47-Locale (z.B. "de-DE"). Default: "de-DE" → DD.MM.YYYY HH:MM.
  locale?: string;
  // IANA-Timezone (z.B. "Europe/Berlin"). Ohne Angabe: lokale Zeitzone.
  timeZone?: string;
  // Intl-Overrides für Sonderfälle (z.B. Sekunden anzeigen).
  // Überschreiben die Default-Felder (day/month/year/hour/minute).
  intl?: Intl.DateTimeFormatOptions;
}

const DEFAULT_LOCALE = "de-DE";

const DEFAULT_INTL: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

/**
 * formatDateTime parst einen ISO-8601-Zeitstempel und formatiert ihn
 * lokalisiert. Bei einem unparsbaren Input wird der Roh-String unverändert
 * zurückgegeben (defensiv, wie die bisherige Inbox-Logik).
 */
export function formatDateTime(iso: string, opts: FormatDateTimeOptions = {}): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;

  const intlOpts: Intl.DateTimeFormatOptions = {
    ...DEFAULT_INTL,
    ...(opts.timeZone ? { timeZone: opts.timeZone } : {}),
    ...(opts.intl ?? {}),
  };

  try {
    return new Intl.DateTimeFormat(opts.locale ?? DEFAULT_LOCALE, intlOpts).format(d);
  } catch {
    // Ungültige locale/timeZone → defensiv auf den Default zurückfallen.
    return new Intl.DateTimeFormat(DEFAULT_LOCALE, DEFAULT_INTL).format(d);
  }
}
